import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';

interface RecentActivity {
  lessonTitle: string;
  moduleName: string;
  action: 'started' | 'video_watched' | 'quiz_passed' | 'completed';
  timestamp: string;
}

interface MyProgressResult {
  modulesCompleted: number;
  totalModules: number;
  lessonsCompleted: number;
  totalLessons: number;
  quizzesPassed: number;
  timeSpentMinutes: number;
  recentActivity: RecentActivity[];
}

export function useMyProgress() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-progress', user?.id],
    queryFn: async (): Promise<MyProgressResult> => {
      if (!user?.id) {
        return {
          modulesCompleted: 0,
          totalModules: 0,
          lessonsCompleted: 0,
          totalLessons: 0,
          quizzesPassed: 0,
          timeSpentMinutes: 0,
          recentActivity: [],
        };
      }

      // Step 1: Fetch all published lessons with their modules
      const { data: lessons, error: lessonsError } = await supabase
        .from('lessons')
        .select(`
          id,
          title,
          module:modules!module_id(id, name, is_published)
        `)
        .eq('is_published', true);

      if (lessonsError) throw lessonsError;

      // Filter to only lessons in published modules
      const publishedLessons = (lessons || []).filter(l => l.module?.is_published);

      // Step 2: Fetch user's lesson_progress records
      const { data: progress, error: progError } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', user.id);

      if (progError) throw progError;

      // Step 3: Fetch user's completion_records
      const { data: completions, error: compError } = await supabase
        .from('completion_records')
        .select(`
          id,
          lesson_id,
          quiz_score,
          accepted_at,
          lesson:lessons!lesson_id(title, module:modules!module_id(name))
        `)
        .eq('user_id', user.id);

      if (compError) throw compError;

      // Calculate stats
      const lessonsCompleted = completions?.length || 0;
      const totalLessons = publishedLessons.length;
      const quizzesPassed = completions?.length || 0;

      // Calculate modules completed
      const moduleMap = new Map<string, { total: number; completed: number; name: string }>();
      
      publishedLessons.forEach(lesson => {
        const moduleId = lesson.module!.id;
        if (!moduleMap.has(moduleId)) {
          moduleMap.set(moduleId, { total: 0, completed: 0, name: lesson.module!.name });
        }
        moduleMap.get(moduleId)!.total++;
      });

      completions?.forEach(c => {
        const lesson = publishedLessons.find(l => l.id === c.lesson_id);
        if (lesson) {
          const moduleId = lesson.module!.id;
          const mod = moduleMap.get(moduleId);
          if (mod) mod.completed++;
        }
      });

      const modulesCompleted = Array.from(moduleMap.values())
        .filter(m => m.completed >= m.total).length;
      const totalModules = moduleMap.size;

      // Calculate time spent (sum of video_duration where video_completed=true)
      const timeSpentMinutes = Math.round(
        (progress || [])
          .filter(p => p.video_completed)
          .reduce((sum, p) => sum + (p.video_duration || 0), 0) / 60
      );

      // Build recent activity
      const activities: RecentActivity[] = [];

      // Add completion events
      completions?.forEach(c => {
        activities.push({
          lessonTitle: c.lesson?.title || 'Unknown',
          moduleName: c.lesson?.module?.name || 'Unknown',
          action: 'completed',
          timestamp: c.accepted_at,
        });
      });

      // Add progress events
      const lessonMap = new Map(publishedLessons.map(l => [l.id, l]));
      
      progress?.forEach(p => {
        const lesson = lessonMap.get(p.lesson_id);
        if (!lesson) return;

        // Don't add events if we already have a completion for this lesson
        const hasCompletion = completions?.some(c => c.lesson_id === p.lesson_id);
        if (hasCompletion) return;

        if (p.quiz_passed && p.quiz_passed_at) {
          activities.push({
            lessonTitle: lesson.title,
            moduleName: lesson.module!.name,
            action: 'quiz_passed',
            timestamp: p.quiz_passed_at,
          });
        } else if (p.video_completed && p.video_completed_at) {
          activities.push({
            lessonTitle: lesson.title,
            moduleName: lesson.module!.name,
            action: 'video_watched',
            timestamp: p.video_completed_at,
          });
        } else if (p.started_at) {
          activities.push({
            lessonTitle: lesson.title,
            moduleName: lesson.module!.name,
            action: 'started',
            timestamp: p.started_at,
          });
        }
      });

      // Sort by timestamp descending and take first 5
      const recentActivity = activities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 5);

      return {
        modulesCompleted,
        totalModules,
        lessonsCompleted,
        totalLessons,
        quizzesPassed,
        timeSpentMinutes,
        recentActivity,
      };
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
  });
}
